import { Autocomplete, TextField, type AutocompleteProps } from '@mui/material';

type AccessibleAutocompleteProps<
  T,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
> = Omit<
  AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
  'renderInput'
> & {
  renderInput?: AutocompleteProps<
    T,
    Multiple,
    DisableClearable,
    FreeSolo
  >['renderInput'];
};

export function AccessibleAutocomplete<
  T,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>({
  renderInput: renderInputProp,
  componentsProps: componentsPropsProp,
  ...autocompleteProps
}: AccessibleAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>) {
  const componentsProps = {
    ...componentsPropsProp,
    clearIndicator: {
      ...componentsPropsProp?.clearIndicator,
      tabIndex: 0,
      sx: { visibility: 'visible' },
    },
  };

  const defaultRenderInput: NonNullable<
    AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>['renderInput']
  > = (params) => <TextField {...params} />;

  const renderInput = (params: Parameters<typeof defaultRenderInput>[0]) => {
    const { onKeyDown: originalOnKeyDown, ...restInputProps } =
      params.inputProps as typeof params.inputProps & {
        onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
      };

    const enhancedParams = {
      ...params,
      inputProps: {
        ...restInputProps,
        onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
          const inputProps = params.inputProps as unknown as Record<string, unknown>;
          const isExpanded =
            inputProps['aria-expanded'] === true ||
            inputProps['aria-expanded'] === 'true';
          if (e.key === ' ' && isExpanded) {
            const listboxId = inputProps['aria-controls'] as string | undefined;
            const listbox = listboxId
              ? document.getElementById(listboxId)
              : null;
            if (listbox) {
              const option = listbox.querySelector(
                '[role="option"].Mui-focused'
              );
              if (option) {
                e.preventDefault();
                (option as HTMLElement).click();
                return;
              }
            }
            originalOnKeyDown?.(e);
          } else {
            originalOnKeyDown?.(e);
          }
        },
      },
    };

    return (renderInputProp ?? defaultRenderInput)(enhancedParams);
  };

  return (
    <Autocomplete
      {...autocompleteProps}
      componentsProps={componentsProps}
      renderInput={renderInput}
    />
  );
}
